/* eslint-disable @ngrx/avoid-combining-selectors */
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize, tap, catchError, delay, take, takeUntil, map, withLatestFrom } from 'rxjs/operators';
import { combineLatest, EMPTY, Observable, of, Subject } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { ConfigService } from 'src/app/services/config/config.service';
import { Store } from '@ngrx/store';
import { StsSelectors } from 'src/app/@store/selectors/sts.selectors';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { EPassForm } from 'src/app/models/password.model';
import { passwordValidation } from 'src/app/modules/shared/regex-pattern';
import { ApiService } from '../../../services/api/api.service';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  loading = false;
  isMatched = false;
  showSpin = true;
  showForm = false;
  regId = '';
  seqId = '';
  returnUrl = '';
  showError = false;
  submitted = false;
  passwordFormatInvalid: boolean = false;
  useNewNav$: Observable<boolean> = this.store.select(StsSelectors.useNewNav);

  isITTenant$ = this.store.select(StsSelectors.isITTenant);
  isGlobal$ = this.store.select(StsSelectors.isGlobalTenant);

  isDXP$ = combineLatest([
    this.store.select(StsSelectors.isUkShfyInstance),
    this.isITTenant$,
    this.isGlobal$,
  ]).pipe(map(([isShfy, isIT, isGlobal]) => isShfy || isIT || isGlobal));

  passwordFormG = new UntypedFormGroup({
    authenticationProvider: new UntypedFormControl('local'),
    schemeProvider: new UntypedFormControl(''),
    newPassword: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(passwordValidation),
    ]),
    confirmPassword: new UntypedFormControl('', [Validators.required]),
  });

  private readonly onDestroy$ = new Subject<void>();


  isIndigoYellowUI$ = of(true).pipe(
    withLatestFrom(this.isGlobal$, this.isITTenant$),
    map(([, global, it]) => {
      return global || it;
    }),
  );

  constructor(
    private readonly api: ApiService,
    private readonly activeRoute: ActivatedRoute,
    private readonly toast: ToastrService,
    @Inject(Window) private readonly window,
    private readonly translocoService: TranslocoService,
    private readonly config: ConfigService,
    private readonly store: Store,
  ) {}

  ngOnInit() {
    this.passwordFormG.setValidators(this.checkPasswords);

    this.activeRoute.queryParams
      .pipe(
        take(1),
        tap(params => {
          this.regId = params.id;
          this.seqId = params.seq;
          this.returnUrl = atob(params.returnUrl || '');

          this.loadClaims();
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  loadClaims() {
    this.api
      .get('/manage/accounts/verify_forgot_password', { id: this.regId, seq: this.seqId })
      .subscribe(d => {
        this.showSpin = false;
        this.showForm = d;
      });
  }

  // eslint-disable-next-line max-lines-per-function
  setAccountPassword() {
    const f = this.passwordFormG.getRawValue();

    this.loading = true;
    this.passwordFormatInvalid = false;
    this.api
      .put(`/manage/accounts/forgot_password`, {
        UserId: this.regId,
        NewPassword: f[EPassForm.CONFIRM_PASS],
        Sequence: this.seqId,
      })
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
        tap(() => {
          this.toast.success(
            this.translocoService.translate(
              'successfullyUpdatedPasswordText',
              {},
              'forgotPassword',
            ),
            this.translocoService.translate(
              'successfullyUpdatedPasswordTitle',
              {},
              'forgotPassword',
            ),
          );
          this.submitted = true;
        }),
        delay(2000),
        tap(() => {
          this.passwordFormG.reset();
          this.passwordFormG.markAsPristine();

          this.window.location.href = `${this.config._envConfig.BaseAuthUrl}/Account/Login`;
        }),
        catchError(err => {
          if (
            err &&
            err.error &&
            typeof err.error.ErrorMessage === 'string' &&
            err.error.ErrorMessage.indexOf('Incorrect Password Format') > -1
          ) {
            this.toast.error(
              this.translocoService.translate('incorrectPasswordFormat', {}, 'security'),
            );
            this.passwordFormatInvalid = true;
            return;
          }
          const errMessage =
            err && err.error && err.error.ErrorMessage
              ? err.error.ErrorMessage
              : this.translocoService.translate('errorUpdatingPasswordText', {}, 'forgotPassword');
          this.toast.error(
            this.translocoService.translate('errorUpdatingPasswordText', {}, 'forgotPassword'),
            this.translocoService.translate('errorOccuredTitle', {}, 'general'),
          );
          console.error(errMessage || err);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  redirectToContactSynlab() {
    this.window.location.href = this.config._envConfig.BaseProfileUrl;
  }

  get EPassForm(): typeof EPassForm {
    return EPassForm;
  }

  public checkPasswords: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    let pass = group.get(EPassForm.NEW_PASS).value;
    let confirmPass = group.get(EPassForm.CONFIRM_PASS).value;
    return pass === confirmPass || confirmPass == '' ? null : { [EPassForm.CONFIRM_PASS]: true };
  };
}
